import { ReactElement, useEffect } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { modalStatusAtom, selectedAccountAtom } from 'store/atoms'

import SyfDisputes, {
  AuthenticationRedirectProps,
  DisputeFeature,
  TransactionProps
} from 'digital-disputes'
import { useAccountSummary } from 'const/syfApiQueries'
import getMFEConfig from 'helpers/getMFEConfig'
import useHandleNavigate from 'hooks/useHandleNavigate'
import { getBrandRefreshPhase } from 'helpers/getBrandRefreshPhase'
import { handleMFEAuthenticationRedirect } from 'helpers/authenticationRedirect'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import triggerAnalyticsEvent, {
  setAnalyticsDefaults
} from 'helpers/triggerAnalyticsEvent'
import { triggerOnCloseAnalytics } from 'helpers/triggerOnCloseAnalytics'
import useProfileNavigate from 'hooks/useProfileNavigate'

const DisputesModal = (): ReactElement => {
  const [{ modalId, metadata }] = useAtom(modalStatusAtom)
  const selectedAccount = useAtomValue(selectedAccountAtom)
  const { handleNavigate } = useHandleNavigate()
  const { data: { product } = {} } = useAccountSummary(selectedAccount)

  const disputesConfig = getMFEConfig('disputes')
  const fraudDisputesEnabled = isFeatureFlagEnabled('isFraudDisputesEnabled')

  const transaction = metadata?.transaction as TransactionProps | undefined

  const onActivityRoute = metadata?.currentRouteActivity as boolean | undefined

  useEffect(() => {
    if (modalId !== 'DisputeATransaction') return
    if (!product?.clientId) return
    setAnalyticsDefaults({ ClientName: product.clientId })
  }, [modalId, product?.clientId])

  const handleAuthFlow = (options: AuthenticationRedirectProps) => {
    handleMFEAuthenticationRedirect({
      navigateTo: options.navigateTo,
      modal: 'DisputeATransaction',
      scope: options.scope
    })
  }

  const { handleProfileClick } = useProfileNavigate()

  const handleCloseAnalytics = () => {
    if (onActivityRoute) {
      setAnalyticsDefaults({ PageKind: 'activity' })
      triggerAnalyticsEvent({
        PageFunction: 'activity',
        PageKind: 'activity'
      })
      return
    }

    triggerOnCloseAnalytics()
  }

  return modalId === 'DisputeATransaction' ? (
    <SyfDisputes
      disputesMFEConfig={disputesConfig}
      accountId={selectedAccount}
      feature={DisputeFeature.Questionnaire}
      navigationHandler={handleNavigate}
      returnToParent={null}
      handleParentCallback={handleCloseAnalytics}
      brandRefreshPhase={getBrandRefreshPhase()}
      transaction={transaction}
      isFraudDisputeExperienceEnabled
      fraudDisputesEnabled={fraudDisputesEnabled}
      authenticationRedirect={handleAuthFlow}
      handleProfileClick={handleProfileClick}
    />
  ) : null
}

export default DisputesModal
